import {
    GetTopicResponse,
    LoginOrigin,
} from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import { getSocialMediaImageForTopic } from '../helpers/files';
import { logWithAmplitude } from '../helpers/api/api';
import { getMainPage } from '../helpers/page-name';

@customElement('set-default-map-template-component')
export class SetDefaultMapTemplateComponent extends LitElement {
    static amplitude_callsite: string = 'Set Default Map Template';

    @property({ type: String })
    emailAddress?: string;

    @property({ type: String })
    one_time_code?: string;

    @property({ type: Boolean })
    show_one_time_code: boolean = false;

    @property({ type: Object })
    currentTopic: GetTopicResponse | undefined;

    @property({ type: Boolean })
    selectTemplateDone: boolean = false;

    @query('#search')
    searchElement?: TextField;

    @state()
    topics: GetTopicResponse[] = [];

    @state()
    topicsToRender: GetTopicResponse[] = [];

    private readonly bindAccountInfo = new StateController(this, accountInfo);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topics')) {
            this.topicsToRender = this.topics;
        }
    }

    private readonly searchChanged = debounce(this.topicSearchChangedInternal, 500);
    private topicSearchChangedInternal() {
        const searchText = this.searchElement?.value?.trim().toLocaleLowerCase() ?? '';

        this.topicsToRender = this.topics.filter((topic) => {
            return topic.name.toLocaleLowerCase().includes(searchText);
        });

        logWithAmplitude(SetDefaultMapTemplateComponent.amplitude_callsite, 'Topic Search', {
            search: searchText
        });
    }

    handleChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    handleChangeOneTimeCode(e: any) {
        this.one_time_code = e.detail.value;
    }

    private async handleSubmit() {
        const email = this.emailAddress ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(SetDefaultMapTemplateComponent.amplitude_callsite, email);
            this.show_one_time_code = true;
            this.requestUpdate();
        }, 'An error has occurred.  Context: could not request an authentication token.');
    }

    private async handleSubmitOneTimeCode(organization_id?: string) {
        const email = this.emailAddress ?? '';
        const one_time_code = this.one_time_code ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                SetDefaultMapTemplateComponent.amplitude_callsite,
                email,
                one_time_code,
                undefined,
                LoginOrigin.SigninModule
            );
            await accountInfo.instantiateAccountData();
            const separator = !window.location.href.includes('?') ? '?' : '&';
            window.location.href = window.location.href + separator + 'addToModal=true';
        }, 'An error has occurred.  Context: could not upgrade the authentication token.');
    }

    private async handleNewEmail() {
        await accountInfo.logout();
        this.show_one_time_code = false;
        this.requestUpdate();
    }

    private onSelectTopic(topic: GetTopicResponse) {
        this.dispatchEvent(new CustomEvent('on-select-topic', {
            detail: {
                id: topic._id.$oid
            }
        }));
    }

    private onClose() {
        this.dispatchEvent(new CustomEvent('close-modal'));
    }

    private viewMap() {
        if (isDefined(this.currentTopic)) {
            window.open(this.currentTopic?.cc_write_link, '_blank');
        }
    }


    render() {
        const modalTitle = 'Select a map to set default template';
        let endTitle = 'The template has been set successfully';

        if (isDefined(this.currentTopic)) {
            endTitle = 'The template has been changed successfully';
        }

        if (this.selectTemplateDone) {
            return html`
                <div class="modalContainer">
                    <div class="allDone">
                        <mwc-icon>thumb_up</mwc-icon>
                        <h3>${endTitle}</h3>
                    </div>
                    <div class="actionButtonGroup">
                        <form-button size="md" fullWidth variant="outline" @click="${this.viewMap}">View Map</form-button>
                        <form-button size="md" fullWidth @click="${this.onClose}">Close</form-button>
                    </div>
                </div>
                <error-toast id="errorToast"></error-toast>
            `;
        }

        return html`
            <div class="modalContainer">
                <h3 class="modalTitle">${modalTitle}</h3>
                <div class="scrollableContent">
                    <div class="topicsWrapper">
                        <div class="form-input-button-wrapper">
                            <mwc-textfield
                                id="search"
                                class="input"
                                label="Search"
                                @input="${this.searchChanged}"
                            >
                            </mwc-textfield>
                        </div>
                        <div>
                            ${this.topicsToRender.map((topic) => {
                                const backgroundImage = isDefined(topic)
                                    ? getSocialMediaImageForTopic(topic)
                                    : '';
                                return html` <div
                                    class="topic-item ${topic._id.$oid === this.currentTopic?._id.$oid ? 'topic-selected' : ''}"
                                    @click="${() => this.onSelectTopic(topic)}"
                                >
                                    <div class="topic-img">
                                        <img
                                            src="${backgroundImage}"
                                            class="backpack-card__image"
                                            alt="card-img"
                                        />
                                    </div>
                                    <div class="topic-content">
                                        <div class="topic-metadata">
                                            <span class="metadata-item">
                                                <mwc-icon class="metadata-icon" outlined
                                                    >place</mwc-icon
                                                >
                                                ${topic?.statistics
                                                    .approximate_point_count}
                                                points
                                            </span>
                                            <span class="metadata-item">
                                                <mwc-icon class="metadata-icon" outlined
                                                    >visibility</mwc-icon
                                                >
                                                ${topic?.statistics
                                                    .approximate_topic_views}
                                                views
                                            </span>
                                        </div>
                                        <h4 class="topic-name">${topic.name}</h4>
                                    </div>
                                    ${topic._id.$oid === this.currentTopic?._id.$oid ? html`
                                        <mwc-icon class="selected-icon">thumb_up</mwc-icon>
                                    ` : ''}
                                </div>`;
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .modalContainer {
                display: flex;
                flex-direction: column;
                width: 550px;
                max-width: 100%;
            }
            .authForm {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .scrollableContent {
                flex: 1;
                overflow: auto;
            }
            /* width */
            .scrollableContent::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .scrollableContent::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .scrollableContent::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }
            .topic-item {
                display: flex;
                margin: 1rem 0;
                max-height: 100px;
                border-radius: 0.75rem;
                overflow: hidden;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
                position: relative;
            }
            .topic-item:hover {
                background-color: var(--theme-lightest-color);
            }
            .topic-item.topic-selected .topic-content {
                background-color: rgba(16, 44, 61, 0.05);
            }
            .topic-item.topic-selected .selected-icon {
                position: absolute;
                top: 1rem;
                right: 1rem;
                color: rgba(16, 44, 61, 0.8);
            }
            .backpack-card__image, .topic-img {
                max-height: 100px;
            }
            .metadata-item {
                display: flex;
                align-items: center;
                color: var(--theme-light-text-color);
                font-size: 14px;
                gap: 0.25rem;
            }
            .metadata-icon {
                color: var(--theme-lighter-text-color);
                --mdc-icon-size: 12px;
            }
            .topic-metadata {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }
            .topic-content {
                flex: 1;
                padding: 0.75rem 1.5rem;
            }
            .topic-name {
                margin: 0.5rem 0;
            }
            .modalTitle {
                text-align: center;
                margin: 1.5rem 0;
                font-size: 24px;
            }
            .form-input-button-wrapper {
                margin-bottom: 1.5rem;
            }
            #search {
                width: 100%;
            }
            .metadata-icon {
                --mdc-icon-size: 18px;
                color: var(--theme-primary-blue);
                font-weight: 600;
            }

            .actionButtonGroup {
                margin-top: 1rem;
                display: flex;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            .actionButtonGroup form-button {
                flex: 1;
            }
            .allDone {
                flex: 1;
                display: flex;
                flex-direction: column;
                margin-top: 2rem;
                justify-content: center;
                align-items: center;
                text-align: center;
            }
        `;
    }
}
